import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@client/components/hooks/use-toast';
import { selectSessionAdvisor, selectAdvisorHierarchyState } from '@client/model/selectors';
import {
  deleteAdvisorHierarchyRelationshipByAdvisorIdRequest,
  fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest,
} from '@client/model/advisorHierarchyModel';

interface UseAdvisorHierarchyDeleteOptions {
  targetAdvisorId?: string | null;
}

/**
 * Hook for managing advisor hierarchy relationship deletion
 * Handles delete confirmation dialog, mutation lifecycle, and toast notifications
 *
 * @param options.targetAdvisorId - Optional advisor ID for admin mode. If provided, manages that advisor's team.
 *                                  If not provided, uses the session advisor (normal mode).
 */
export function useAdvisorHierarchyDelete({
  targetAdvisorId,
}: UseAdvisorHierarchyDeleteOptions = {}) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const { deleteMutationStatus, deleteMutationError } = useSelector(selectAdvisorHierarchyState);

  // Use targetAdvisorId if provided (admin mode), otherwise use session advisor
  const advisorId = targetAdvisorId ?? sessionAdvisor?.advisorId;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [relationshipToDelete, setRelationshipToDelete] =
    useState<AdvisorHierarchyRelationship | null>(null);
  const prevDeletingRef = useRef(false);
  // Track whether this hook initiated the delete operation
  const initiatedDeleteRef = useRef(false);

  // Handle delete mutation completion
  useEffect(() => {
    const isDeleting = deleteMutationStatus === 'loading';

    // Mutation just completed (was deleting, now not) and this hook initiated it
    if (prevDeletingRef.current && !isDeleting && initiatedDeleteRef.current) {
      initiatedDeleteRef.current = false;
      if (deleteMutationError) {
        toast({
          title: 'Error',
          description: deleteMutationError || 'Failed to delete relationship. Please try again.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
      } else if (deleteMutationStatus === 'success') {
        toast({
          title: 'Success',
          description: 'Team member removed successfully.',
          className: '-translate-y-5',
        });
      }
    }

    prevDeletingRef.current = isDeleting;
  }, [deleteMutationStatus, deleteMutationError, dispatch, toast]);

  const handleDelete = (relationship: AdvisorHierarchyRelationship) => {
    setRelationshipToDelete(relationship);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!relationshipToDelete || !advisorId) {
      return;
    }

    const body: DeleteAdvisorHierarchyRelationshipBody = {};

    // Determine which identifier to use based on relationship status
    if (relationshipToDelete.relationshipStatus === 'active') {
      // Active relationship - use subordinateAdvisorId
      if (!relationshipToDelete.advisorId) {
        toast({
          title: 'Error',
          description: 'Unable to delete relationship. Missing advisor information.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
        return;
      }
      body.subordinateAdvisorId = relationshipToDelete.advisorId;
    } else {
      // Sent or declined invitation - use invitationId
      if (!relationshipToDelete.invitationId) {
        toast({
          title: 'Error',
          description: 'Unable to delete invitation. Missing invitation information.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
        return;
      }
      body.invitationId = relationshipToDelete.invitationId;
    }

    initiatedDeleteRef.current = true;
    dispatch(
      deleteAdvisorHierarchyRelationshipByAdvisorIdRequest({
        advisorId,
        ...body,
      }),
    );

    setDeleteDialogOpen(false);
    setRelationshipToDelete(null);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setRelationshipToDelete(null);
  };

  return {
    // Dialog state
    deleteDialogOpen,
    relationshipToDelete,

    // Mutation state
    isDeleting: deleteMutationStatus === 'loading',

    // Actions
    handleDelete,
    confirmDelete,
    cancelDelete,
  };
}

export type UseAdvisorHierarchyDeleteReturn = ReturnType<typeof useAdvisorHierarchyDelete>;
